import { useEffect, useRef, useState, useCallback } from "react";
import api from "../lib/api";

export function useDiagnostics() {
  const [diag, setDiag] = useState(null);
  const [diagnosticsState, setDiagnosticsState] = useState("idle");
  const [diagnosticsError, setDiagnosticsError] = useState("");
  const [diagnosticsElapsedMs, setDiagnosticsElapsedMs] = useState(null);
  const requestId = useRef(0);
  const refresh = useCallback(async ({ signal } = {}) => {
    const currentRequestId = ++requestId.current;
    const startedAt = Date.now();
    setDiagnosticsState("loading");
    setDiagnosticsError("");
    setDiagnosticsElapsedMs(null);
    try {
      const { data } = await api.get("/admin/diagnostics", { signal });
      if (signal?.aborted || currentRequestId !== requestId.current) return null;
      setDiag(data);
      setDiagnosticsElapsedMs(Date.now() - startedAt);
      setDiagnosticsState("ready");
      return data;
    } catch (error) {
      if (signal?.aborted || error?.code === "ERR_CANCELED") return null;
      if (currentRequestId !== requestId.current) return null;
      setDiagnosticsState("error");
      setDiagnosticsError(
        error?.response?.data?.detail || error?.message || "Diagnostics unavailable",
      );
      setDiagnosticsElapsedMs(Date.now() - startedAt);
      return null;
    }
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    refresh({ signal: controller.signal });
    return () => controller.abort();
  }, [refresh]);
  return {
    diag,
    diagnosticsState,
    diagnosticsError,
    diagnosticsElapsedMs,
    refreshDiagnostics: refresh,
  };
}

/**
 * Wrap a destructive admin action in a production-mode confirmation.
 * - In preview/dev, runs straight through.
 * - In production (per /api/admin/diagnostics.is_production), shows a
 *   window.confirm so the admin can't accidentally write to prod.
 */
export function confirmProductionWrite(diag, message) {
  if (!diag || !diag.is_production) return true;
  const dbName = diag.db_name || "production";
  return window.confirm(
    `⚠️ PRODUCTION WRITE\n\n${message}\n\n` +
      `This will modify the connected production database (${dbName}).\n\nProceed?`,
  );
}
